import {
  alpha,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "~/components/loading-button";
import { useColorMode } from "~/providers/color-mode";
import {
  ExceptionFormValidUntil,
  useSetExceptionModal,
} from "./hooks/useSetExceptionDialog";
import { useEffect } from "react";
import { useScope } from "~/hooks/useScope";
import { Flex } from "../Flex";
import { ExceptionFormInput } from "~/components/exceptions/SetExceptionDialog";
import { EntityInfoAsset } from "~/operations";
import { DialogProps } from "@toolpad/core";

export type ExtendExceptionFormInput = {
  exceptionId: string;
  spaceMrn: string;
  validUntil: ExceptionFormValidUntil;
  customValidUntil: number;
  justification: string;
};

export interface ExtendExceptionDialogPayload {
  onSave: (params: ExtendExceptionFormInput) => Promise<void>;
  targetData: {
    targetTitle?: string;
    onAsset?: EntityInfoAsset | null | undefined;
  };
}

export interface ExtendExceptionDialogProps
  extends DialogProps<ExtendExceptionDialogPayload> {}

export function ExtendExceptionDialog(props: ExtendExceptionDialogProps) {
  const { open, onClose } = props;
  const { mode } = useColorMode();
  const { spaceScope } = useScope();
  const { onSave, targetData } = props.payload;

  const {
    register,
    handleSubmit,
    formState,
    reset,
    control,
    setValue,
    watch,
    trigger,
  } = useForm<ExceptionFormInput>({
    mode: "onChange",
    defaultValues: {
      validUntil: ExceptionFormValidUntil.Month6,
      scope: spaceScope?.mrn || "",
      justification: "",
      customValidUntil: 0,
    },
  });

  // set asset MRN as scope when it's an asset
  useEffect(() => {
    if (!spaceScope || !targetData.onAsset) return;

    const scope = Boolean(targetData.onAsset)
      ? targetData.onAsset?.mrn
      : spaceScope?.mrn;

    setValue("scope", scope);
  }, [targetData.onAsset, setValue, open]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const { timeLimitOptions, form, handleClose, isLoading } =
    useSetExceptionModal({
      onSave,
      reset,
      onClose,
      onAsset: targetData?.onAsset,
    });

  const validUntilWatch = watch("validUntil");
  const isCustomDurationSelected =
    validUntilWatch === ExceptionFormValidUntil.Custom;

  useEffect(() => {
    trigger("customValidUntil");
  }, [validUntilWatch]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit(form.handleFormSubmit),
        },
      }}
      onClose={handleClose}
      data-testid="extend-exception-dialog"
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 5,
          pb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
          }}
        >
          Request time extension for {targetData?.targetTitle || ""}
        </Typography>
        <IconButton
          onClick={handleClose}
          data-testid="close-exception-dialog"
          aria-label="Close dialog"
        >
          <CloseIcon sx={{ color: "text.secondary" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 5, pb: 2 }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Select new time limit for this exception below. This will need to be
          approved.
        </Typography>
        <FormControl fullWidth>
          <Typography
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Time Limit
          </Typography>
          <Box sx={{ pl: "11px" }}>
            <Controller
              control={control}
              name="validUntil"
              render={({ field }) => (
                <RadioGroup {...field} row sx={{ mb: 3, gap: 1.25 }}>
                  {timeLimitOptions.map((option) => {
                    const isSelected = option.value === field.value;
                    const isCustomField =
                      option.value === ExceptionFormValidUntil.Custom;
                    return (
                      <>
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={
                            <Radio
                              sx={{ position: "absolute", opacity: 0 }}
                              data-testid={`${option.value}-radio`}
                              role="radio"
                            />
                          }
                          label={option.label}
                          sx={{
                            position: "relative",
                            py: 0.5,
                            px: 1,
                            ...(isCustomField ? { mr: 0 } : {}),
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.2),
                            border: "1px solid",
                            borderColor: "transparent",
                            borderRadius: 1,
                            ...(isSelected && {
                              borderColor: "primary.main",
                              backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.4),
                            }),
                            "& .MuiFormControlLabel-label": {
                              fontWeight: 700,
                            },
                          }}
                        />
                        {isCustomField && (
                          <Flex>
                            <TextField
                              data-testid="exception-custom-duration"
                              type="number"
                              onFocus={() => {
                                setValue(
                                  "validUntil",
                                  ExceptionFormValidUntil.Custom,
                                );
                              }}
                              {...register(`customValidUntil`, {
                                valueAsNumber: true,
                                validate: (value) => {
                                  if (isCustomDurationSelected && value <= 0) {
                                    return "Number of days should be provided";
                                  }
                                  return true;
                                },
                              })}
                              slotProps={{
                                htmlInput: {
                                  min: 0,
                                  sx: { py: 1, px: 2, height: "18px" },
                                },
                              }}
                              variant="outlined"
                              sx={{
                                maxWidth: "72px",
                                boxSizing: "content-box",
                              }}
                              error={Boolean(
                                formState.errors?.customValidUntil,
                              )}
                            />
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{ mx: 2 }}
                            />
                          </Flex>
                        )}
                      </>
                    );
                  })}
                </RadioGroup>
              )}
            />
          </Box>
        </FormControl>
        <FormControl fullWidth>
          <Typography
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Justification{" "}
            <Typography component="span" color="text.secondary">
              (Limit 400 characters)
            </Typography>
          </Typography>
          <TextField
            placeholder="Provide a reason for extension of this exception..."
            multiline
            rows={8}
            {...register("justification", {
              required: "Justification is required",
              maxLength: {
                value: 400,
                message: "Must be 400 characters or fewer",
              },
            })}
            error={Boolean(formState.errors.justification)}
            helperText={formState.errors.justification?.message}
            InputProps={{
              sx: {
                backgroundColor:
                  mode === "light" ? "common.white" : "common.black",
                borderRadius: 1,
              },
            }}
            inputProps={{ maxLength: 400 }}
            data-testid="justification-input"
          />
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 5 }}>
        <LoadingButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={!formState.isValid}
          loading={isLoading}
          buttonText="Request time extension"
          data-test-id="extend-exception-button"
        />
      </DialogActions>
    </Dialog>
  );
}
